import React from "react";
import FilterBar from "@/components/common/FilterBar";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import { useAppData } from "@/contexts/AppDataContext";
import { getUnifiedChartHeight } from "@/lib/utils";
import SlaRankingWidget from "../widgets/agents/SlaRankingWidget";
import StatusOverTimeWidget from "../widgets/agents/StatusOverTimeWidget";
import AgentPerformanceTableWidget from "../widgets/agents/AgentPerformanceTableWidget";
import AgentPerformanceDetailTable from "../widgets/agents/AgentPerformanceDetailTable";
import { getAgentIds } from "@/utils/agentUtils";

const AGENT_LIMIT = 20;

const AgentsTab: React.FC<{ isGeneralMode?: boolean; filterClass?: string }> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.TODAY;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("agent-performance-dashboard", current?.id);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [searchTerm, setSearchTerm] = React.useState<string>(filters?.searchTerm ?? "");
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: filters?.startDate ?? null,
    endDate: filters?.endDate ?? null,
    previousStartDate: filters?.previousStartDate ?? null,
    previousEndDate: filters?.previousEndDate ?? null,
  });
  const { uniqueAgents } = useAppData();

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  const getAgentMicrosoftIds = React.useCallback((agents: string[]): string[] | null => {
    return agents.length > 0 ? agents : agentIds;
  }, [agentIds]);

  // Current date parameters for widgets
  const [currentDateParams, setCurrentDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      setDateRange({
        key: filters?.dateRangeKey,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      });
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        queueMicrosoftIds: null as string[] | null,
        agentMicrosoftIds: getAgentMicrosoftIds(filters.agents),
        groupIds: null as number[] | null,
        timeStamp: new Date().toISOString(),
      };
    }
    const dr = calculateDateRange(defaultDateRangeKey);
    return {
      startDate: dr.from.toISOString(),
      endDate: dr.to.toISOString(),
      previousStartDate: dr.previousFrom.toISOString(),
      previousEndDate: dr.previousTo.toISOString(),
      queueMicrosoftIds: null as string[] | null,
      agentMicrosoftIds: getAgentMicrosoftIds([]),
      groupIds: null as number[] | null,
      timeStamp: new Date().toISOString(),
    };
  });

  // Reset state về mặc định khi filters bị reset/null (ví dụ sau khi reset cache)
  React.useEffect(() => {
    if (!filters) {
      const dr = calculateDateRange(defaultDateRangeKey);
      setDateRange({
        key: defaultDateRangeKey,
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
      });
      setCurrentDateParams({
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
        queueMicrosoftIds: null,
        agentMicrosoftIds: getAgentMicrosoftIds([]),
        groupIds: null,
        timeStamp: new Date().toISOString(),
      });
      setSearchTerm("");
    }
  }, [filters, getAgentMicrosoftIds]);

  // Refresh data when tab becomes active
  React.useEffect(() => {
    if (isGeneralMode) {
      setCurrentDateParams(prev => ({
        ...prev,
        timeStamp: new Date().toISOString()
      }));
    }
  }, [isGeneralMode]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setDateRangeKey(event.dateRange.key);
    
    // Recalculate dates for dynamic date ranges to ensure freshness
    const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
      event.dateRange.key,
      {
        dateRangeKey: event.dateRange.key,
        startDate: event.dateRange.startDate.toISOString(),
        endDate: event.dateRange.endDate.toISOString(),
        previousStartDate: event.dateRange.previousStartDate.toISOString(),
        previousEndDate: event.dateRange.previousEndDate.toISOString(),
      }
    );
    
    setDateRange({
      key: event.dateRange.key,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
    });
    setCurrentDateParams({
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
      queueMicrosoftIds: null as string[] | null,
      agentMicrosoftIds: getAgentMicrosoftIds(event.agents),
      groupIds: null as number[] | null,
      timeStamp: new Date().toISOString(),
    });

    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
      searchTerm: searchTerm,
    });
  };

  // Update currentDateParams when filters change
  React.useEffect(() => {
    if (filters?.agents) {
      setCurrentDateParams(prev => ({
        ...prev,
        agentMicrosoftIds: getAgentMicrosoftIds(filters.agents || []),
        timeStamp: new Date().toISOString()
      }));
    }
  }, [filters?.agents, getAgentMicrosoftIds]);

  // Use ref to maintain stable reference for filters to prevent unnecessary rerenders
  const filtersRef = React.useRef(filters);
  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const handleSearchChange = React.useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);

    const currentFilters = filtersRef.current;
    if (currentFilters) {
      saveFilters({
        ...currentFilters,
        searchTerm: newSearchTerm,
      });
    }
  }, [saveFilters]);

  // Calculate unified chart height for widgets
  const unifiedChartHeight = getUnifiedChartHeight(AGENT_LIMIT, AGENT_LIMIT);

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white p-[8px]">
        <div className={filterClass}>
          <FilterBar
            onFilterChange={handleFilterChange}
            initialDateRangeKey={dateRangeKey}
            initialSelectedQueues={filters?.queues}
            initialSelectedAgents={filters?.agents}
            initialSelectedQueueType={filters?.selectedQueueType}
            initialDateRange={dateRange}
          />
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <AgentPerformanceDetailTable
              dateParams={{
                startDate: currentDateParams.startDate,
                endDate: currentDateParams.endDate,
                previousStartDate: currentDateParams.previousStartDate,
                previousEndDate: currentDateParams.previousEndDate,
                queueMicrosoftIds: currentDateParams.queueMicrosoftIds,
                agentMicrosoftIds: currentDateParams.agentMicrosoftIds,
                groupIds: currentDateParams.groupIds,
                timeStamp: currentDateParams.timeStamp,
              }}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              limit={10}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SlaRankingWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              previousStartDate={currentDateParams.previousStartDate}
              previousEndDate={currentDateParams.previousEndDate}
              queueMicrosoftIds={currentDateParams.queueMicrosoftIds}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
              groupIds={currentDateParams.groupIds}
              unifiedChartHeight={unifiedChartHeight}
              agentLimit={AGENT_LIMIT}
            />
            <StatusOverTimeWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              previousStartDate={currentDateParams.previousStartDate}
              previousEndDate={currentDateParams.previousEndDate}
              queueMicrosoftIds={currentDateParams.queueMicrosoftIds}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
              groupIds={currentDateParams.groupIds}
              unifiedChartHeight={unifiedChartHeight}
              agentLimit={AGENT_LIMIT}
            />
          </div>

          <div>
            <AgentPerformanceTableWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsTab;


